import {ProvisionTransactionStatus} from './provision-transaction-status.enum';
import {provisionEnumLabelFallback} from './provision-enum-label-fallback';

/**
 * German presentation companion of {@link ProvisionTransactionStatus} — the SINGLE place in the cp
 * frontend that NAMES a booking status, following the pattern of `provision-run-status-label.ts`.
 * The labels mirror the backend `@UiEnumValueInfo(uiLabel = …)`.
 */
export const CP_TRANSACTION_STATUS_LABELS: Readonly<Record<ProvisionTransactionStatus, string>> = {
    [ProvisionTransactionStatus.CALCULATED]: 'Berechnet',
    [ProvisionTransactionStatus.ACTIVE]: 'Aktiv',
    [ProvisionTransactionStatus.CANCELLED]: 'Verworfen'
};

/** PrimeNG `p-tag` severities, mirroring the label map one-to-one. */
export type CpTransactionStatusSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary';

/**
 * Tag severity per status — a READABILITY cue only.
 *
 * It is deliberately not a statement about money: `CANCELLED` is `secondary` (grey) rather than
 * `danger`, because a discarded booking moves nothing (GR-3) and a red tag beside a struck-through
 * line would read as a loss. Whether an amount is good or bad is decided by the SIGN of the
 * delivered `signedAmount` / `runningBalance` and by nothing else (FD-1 / B1).
 */
export const CP_TRANSACTION_STATUS_SEVERITIES: Readonly<Record<ProvisionTransactionStatus, CpTransactionStatusSeverity>> = {
    [ProvisionTransactionStatus.CALCULATED]: 'info',
    [ProvisionTransactionStatus.ACTIVE]: 'success',
    [ProvisionTransactionStatus.CANCELLED]: 'secondary'
};

/** German label of a booking status; an unmapped constant stays VISIBLE, never blank. */
export function getProvisionTransactionStatusLabel(status: ProvisionTransactionStatus): string {
    return CP_TRANSACTION_STATUS_LABELS[status]
        ?? provisionEnumLabelFallback(ProvisionTransactionStatus, status);
}

/** PrimeNG tag severity of a booking status; an unmapped constant renders neutral. */
export function getProvisionTransactionStatusSeverity(status: ProvisionTransactionStatus): CpTransactionStatusSeverity {
    return CP_TRANSACTION_STATUS_SEVERITIES[status] ?? 'secondary';
}
